import React, { useCallback, useContext, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { COUNTRY_SELECT_OPTIONS, INDUSTRY_SELECT_OPTIONS } from 'constants/firmographics'

import {
  Box,
  Button,
  ButtonGroup,
  Flex,
  Header,
  Select,
  SelectOption,
  StatusMessage,
  Text,
  Toggle
} from '@driftt/dds'

import { setAccountFitFirmographics } from 'api/AccountScoringAPI'
import { setOrgProperty } from 'api/CustomerAPI'
import { ICPSettingsContext } from 'context/icpSettingsContext'
import { companySizeOptions } from 'hooks/useICPSettings'
import { getCurrentOrg } from 'selectors'
import { AccountFitSetupStatus } from 'utils/features'
import { segmentTrack, TrackType } from 'utils/tracking'

const EMPTY_SETTINGS = { EmployeeCount: [], Countries: [], Industries: [] }

const AccountFitPreferences = ({ onPreferencesUpdated }: { onPreferencesUpdated: () => void }) => {
  const org = useSelector(getCurrentOrg)
  const loc = localStorage.getItem('firmographic_settings_temp')
  const localICPSettings = loc ? JSON.parse(loc) : EMPTY_SETTINGS

  const [hasChanges, setHasChanges] = useState<boolean>(false)

  const {
    accountFitEnabled,
    setAccountFitEnabled,
    setAccountFitSetup,
    noParameters,
    scoringInProgress,
    companySizes,
    setCompanySizes,
    countries,
    setCountries,
    industries,
    setIndustries,
    applyDataToSelects,
    error,
    defaultData,
    scoringWorkflowError
  } = useContext(ICPSettingsContext)

  const handleToggle = useCallback(
    (checked: boolean) => {
      setAccountFitEnabled(checked)
      setOrgProperty(org.id, 'accountFitEnabled', checked)
      onPreferencesUpdated()
      segmentTrack(TrackType.ACCOUNT_FIT_SCORE_TOGGLED, {
        orgId: org.id,
        enabled: checked
      })
    },
    [setAccountFitEnabled, org, onPreferencesUpdated]
  )

  const getValuesFromOptions = (options: SelectOption<string>[]) =>
    options.map(option => option.value)
  const handleSelectChange = useCallback(
    (value: SelectOption<string>[], metaData) => {
      setHasChanges(true)
      switch (metaData.name) {
        case 'dds--select-employeeCount':
          setCompanySizes(value)
          return
        case 'dds--select-country':
          setCountries(value)
          return
        case 'dds--select-industry':
          setIndustries(value)
          return
        default:
          return
      }
    },
    [setCompanySizes, setCountries, setIndustries]
  )

  const onSave = () => {
    const sanitizedCompanySizes = getValuesFromOptions(companySizes)
    const sanitizedCountries = getValuesFromOptions(countries)
    const sanitizedIndustries = getValuesFromOptions(industries)

    const reqBody = {
      EmployeeCount: sanitizedCompanySizes,
      Countries: sanitizedCountries,
      Industries: sanitizedIndustries
    }

    setAccountFitFirmographics(reqBody)
    setAccountFitSetup(AccountFitSetupStatus.NOT_COMPLETE)
    setOrgProperty(org.id, 'accountFitSetup', AccountFitSetupStatus.NOT_COMPLETE)
    localStorage.setItem(
      'firmographic_settings_temp',
      JSON.stringify({ EmployeeCount: companySizes, Countries: countries, Industries: industries })
    )
    setHasChanges(false)
    segmentTrack(TrackType.ACCOUNT_FIT_ICP_SETTINGS_CHANGED, {
      orgId: org.id,
      employeeCount: sanitizedCompanySizes,
      countries: sanitizedCountries,
      industries: sanitizedIndustries
    })
  }

  const onCancel = () => {
    applyDataToSelects(defaultData)
    setHasChanges(false)
  }

  const allValuesAreEmpty = useMemo(
    () => companySizes.length + countries.length + industries.length < 1,
    [companySizes, countries, industries]
  )

  const ICPSelect = ({
    placeholder,
    label,
    options,
    value
  }: {
    placeholder: string
    label: string
    options: SelectOption[]
    value: SelectOption[]
  }) => (
    <Select
      options={options}
      value={value}
      placeholder={placeholder}
      label={label}
      size="fullWidth"
      // @ts-expect-error newValue in onChange is unknown
      onChange={handleSelectChange}
      allowSelectAll
      hideLabel
      multi
      isClearable
      isDisabled={!accountFitEnabled || scoringInProgress || error || scoringWorkflowError}
      menuPortalTarget={document.getElementsByClassName('preferencesModal')[0] as HTMLElement}
      menuPosition="fixed"
    />
  )

  const companySizesToDisplay = scoringInProgress ? localICPSettings.EmployeeCount : companySizes
  const countriesToDisplay = scoringInProgress ? localICPSettings.Countries : countries
  const industriesToDisplay = scoringInProgress ? localICPSettings.Industries : industries

  return (
    <Box>
      <Header variant="h3" css={{ marginBottom: '$1' }}>
        Account fit
      </Header>
      <Text variant="subtitle2" css={{ marginBottom: '8px' }}>
        Drift&apos;s account fit score is powered by AI and analyzes how well an account matches
        other accounts your organization sells to.
      </Text>
      <Box css={{ border: '1px solid $gray3', borderRadius: '3px', padding: '16px' }}>
        <Flex alignItems="flex-start" css={{ marginBottom: '8px' }}>
          <Box>
            <Text variant="bodyBold" css={{ marginBottom: '8px' }}>
              Show account fit in Drift&apos;s Prospector tools
            </Text>
            <Text variant="subtitle2">
              Account fit scores will be visible to all teammates in Drift. For the best results
              fill in all fields. It may take up to 24 hours to generate new account fit scores when
              you modify your ICP below.
            </Text>
          </Box>
          <Flex css={{ width: '64px' }}>
            <Toggle name="account fit toggle" onChange={handleToggle} checked={accountFitEnabled} />
          </Flex>
        </Flex>
        {noParameters && (
          <Flex css={{ marginBottom: '4px' }}>
            <StatusMessage variant="info">
              Add parameters below if you would like us to further customize your scoring model.
            </StatusMessage>
          </Flex>
        )}
        {scoringInProgress && (
          <Flex css={{ marginBottom: '4px' }}>
            <StatusMessage variant="info">
              Your settings are temporarily locked while we complete the scoring process.
            </StatusMessage>
          </Flex>
        )}
        {scoringWorkflowError && (
          <Flex css={{ marginBottom: '4px' }}>
            <StatusMessage variant="error">
              An error occurred while processing your modified ICP parameters. We are working on a fix - try again later.
            </StatusMessage>
          </Flex>
        )}
        <Box css={{ background: '$gray2', borderRadius: '3px', padding: '16px' }}>
          <Text css={{ marginBottom: '8px' }}>We want to prioritize accounts...</Text>
          {/* employee count */}
          <Flex alignItems="center" css={{ marginBottom: '8px' }}>
            <Text css={{ textWrap: 'nowrap' }}>whose employee count is</Text>
            <ICPSelect
              placeholder={'Select employee count'}
              label={'employeeCount'}
              options={(companySizeOptions as SelectOption[]) ?? []}
              value={companySizesToDisplay ?? []}
            />
          </Flex>
          {/* location */}
          <Flex alignItems="center" css={{ marginBottom: '8px' }}>
            <Text css={{ textWrap: 'nowrap' }}>operating in</Text>
            <ICPSelect
              placeholder={'Select countries'}
              label={'country'}
              options={COUNTRY_SELECT_OPTIONS as SelectOption[]}
              value={countriesToDisplay ?? []}
            />
          </Flex>
          {/* industries */}
          <Flex alignItems="center">
            <Text css={{ textWrap: 'nowrap' }}>in these industries</Text>
            <ICPSelect
              placeholder={'Select industries'}
              label={'industry'}
              options={INDUSTRY_SELECT_OPTIONS as SelectOption[]}
              value={industriesToDisplay ?? []}
            />
          </Flex>
          {accountFitEnabled && !scoringInProgress && !error && (
            <Box css={{ marginTop: '16px' }}>
              <ButtonGroup>
                <Button onClick={onCancel} variant="secondary" disabled={!hasChanges}>
                  Cancel
                </Button>
                <Button onClick={onSave} disabled={allValuesAreEmpty || !hasChanges}>
                  Save
                </Button>
              </ButtonGroup>
              <Flex css={{ marginTop: '8px' }} justifyContent="flex-end">
                <Text variant="metadata">
                  When you save your changes, the settings will be locked until the scoring process
                  is complete.
                </Text>
              </Flex>
            </Box>
          )}
          {error && (
            <Box css={{ marginTop: '16px' }}>
              <StatusMessage variant="error">
                There was a problem fetching your settings.
              </StatusMessage>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  )
}

export default AccountFitPreferences
